import type { PapelUsuario } from '@pgcustom/shared-types';

/**
 * Formato mínimo de usuário autenticado devolvido por
 * `POST /api/v1/auth/login` e `GET /api/v1/auth/me`
 * (`apps/api/src/modules/auth/auth.types.ts`, `UsuarioAutenticado`).
 * Não é reexportado de `@pgcustom/shared-types` porque esse tipo é
 * interno ao módulo Auth da API, não um contrato de domínio
 * compartilhado — o frontend define aqui exatamente o formato que
 * realmente recebe pela rede, nunca importando de dentro de `apps/api`.
 */
export interface UsuarioSessao {
  id: string;
  nome: string;
  email: string;
  papel: PapelUsuario;
  /** `true` quando a senha atual foi definida por um operador (não pelo próprio usuário) — o app deve bloquear qualquer outra tela até a troca ser concluída (`POST /auth/trocar-senha-obrigatoria`). */
  deveTrocarSenha: boolean;
}

type OuvinteSessao = (usuario: UsuarioSessao | null) => void;

/**
 * Fonte única, em memória, de "quem está autenticado nesta aba".
 * NUNCA persiste em `localStorage`/`sessionStorage` — a sessão real
 * vive inteiramente no cookie `httpOnly` `pg_session`, controlado pelo
 * backend (`docs/PROJECT_STATE.md`, seção 14); este store só espelha,
 * para a UI reagir, o que `GET /api/v1/auth/me` respondeu por último.
 * Um recarregamento de página sempre perde este estado por design —
 * `main.ts` sempre revalida contra `/me` na inicialização.
 */
export class SessionStore {
  private usuario: UsuarioSessao | null = null;
  private readonly ouvintes = new Set<OuvinteSessao>();

  obterUsuario(): UsuarioSessao | null {
    return this.usuario;
  }

  estaAutenticado(): boolean {
    return this.usuario !== null;
  }

  definirUsuario(usuario: UsuarioSessao | null): void {
    this.usuario = usuario;
    this.ouvintes.forEach((ouvinte) => ouvinte(usuario));
  }

  /** Retorna a função de cancelamento da inscrição. */
  inscrever(ouvinte: OuvinteSessao): () => void {
    this.ouvintes.add(ouvinte);
    return () => this.ouvintes.delete(ouvinte);
  }
}

export const sessionStore = new SessionStore();
